import { useContext, useEffect, useRef } from 'react'
import { LoadingWrapperContext } from 'src/modules/core/context'
import { AuthContext } from 'src/modules/user/context'
import { AuthService } from 'src/services/auth.service'
import { TokenService } from 'src/services/token.service'
import { resolveNextDestination } from './next-destination'

/**
 * Redeems a one-time login code minted by the embedded Shopify app, so the
 * merchant lands in the account connected to their shop rather than in
 * whichever account their browser's Google session points at.
 *
 * Deliberately **not** modelled on `impersonate.tsx`. That page hard-codes the
 * dashboard, reads no destination, and on failure signs the browser out. Here a
 * failure must leave an existing session exactly as it found it: a merchant who
 * already had a live, correct session in this tab must not lose it because a
 * 60-second code aged out.
 */
export function ConnectedAppLogin() {
  const hasStartedRef = useRef(false)
  const { authenticateUser } = useContext(AuthContext)
  const { enableLoading, setLoadingMessage } = useContext(LoadingWrapperContext)

  useEffect(() => {
    if (hasStartedRef.current) return
    hasStartedRef.current = true

    const params = new URLSearchParams(window.location.search)
    const code = params.get('code')
    const next = resolveNextDestination(params.get('next'))

    // FIRST ACT, before the exchange and before any analytics or error report.
    // The URL this tab was opened with carries the code, so it reaches history,
    // the referrer of anything the SPA loads next, and any boot-time page view.
    stripHandoffParamsFromAddressBar()

    enableLoading()
    setLoadingMessage('Authenticating...')

    void redeemThenLand(code, next, authenticateUser)
  }, [authenticateUser, enableLoading, setLoadingMessage])

  return null
}

function stripHandoffParamsFromAddressBar(): void {
  try {
    const url = new URL(window.location.href)
    url.searchParams.delete('code')
    url.searchParams.delete('next')
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`)
  } catch {
    // A browser that cannot rewrite its own address bar is not a reason to fail
    // the handoff. The code is still single-use and seconds-lived.
  }
}

async function redeemThenLand(code: string | null, next: string, authenticateUser: () => void): Promise<void> {
  try {
    if (code) {
      const tokens = await AuthService.exchangeConnectedAppLoginCode(code)
      if (tokens?.access_token && tokens?.refresh_token) {
        TokenService.getInstance().setToken(tokens.access_token, tokens.refresh_token)
        authenticateUser()
      }
    }
  } catch {
    // A code can arrive missing, malformed, already consumed (a reload, a
    // duplicated tab, a prefetching extension) or expired by a slow boot.
    // Clear nothing, sign nobody out, raise nothing: the destination's own
    // routing sends an unauthenticated browser to manual sign-in, which is the
    // behaviour being replaced and the accepted floor, and leaves an
    // already-signed-in browser alone.
    //
    // Logged, and deliberately without the code or any part of it, so a
    // "it put me in the wrong account" report has a client-side counterpart to
    // the monolith's redeem audit line.
    console.warn('connected-app-login: code exchange failed, landing signed out')
  }

  // A full navigation, never react-router's `navigate()`: the redeem page lives
  // under `/onboarding`, and the router would otherwise re-evaluate this route
  // the instant the session lands and bounce it to the dashboard.
  //
  // `replace`, not an assignment: the tab was opened on this URL, and leaving
  // it in session history means Back returns to a redeem page whose code is
  // spent, which immediately pushes forward to `/dashboard/home` and traps the
  // merchant away from where they were.
  window.location.replace(next)
}
